import { useCallback, useEffect, useMemo, useState } from 'react'
import { CartItemType } from '../utils/sharedTypes'

// Le panier vit côté client jusqu'au « Continuer » : on ne crée les Registration qu'à ce moment,
// pour éviter de laisser des inscriptions fantômes derrière chaque hésitation du prospect.
//
// La clé est scopée pour que le panier du lot 2 (arrivée à froid, sans groupe encore créé) puisse
// coexister avec celui d'un groupe existant, et versionnée pour pouvoir changer la forme de
// CartItemType sans casser les paniers déjà en cours.
const CART_STORAGE_PREFIX = 'walter_cart_v1'

// Personne ne suit sérieusement plus de 3 thèmes de front : c'est l'objectif affiché par la
// progression et les CTA. Ce n'est pas une limite dure, on n'empêche pas d'en ajouter davantage.
export const CART_TARGET = 3

const storageKey = (scope: string | undefined): string => `${CART_STORAGE_PREFIX}:${scope || 'cold_start'}`

const readCart = (key: string): CartItemType[] => {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is CartItemType =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as CartItemType).productCode === 'string' &&
        typeof (item as CartItemType).themeCode === 'string',
    )
  } catch {
    // localStorage indisponible (navigation privée sur certains navigateurs) ou contenu corrompu :
    // on repart d'un panier vide plutôt que de casser la page.
    return []
  }
}

type UseCartReturnType = {
  items: CartItemType[]
  add: (item: CartItemType) => void
  remove: (productCode: string) => void
  toggle: (item: CartItemType) => void
  has: (productCode: string) => boolean
  clear: () => void
  count: number
  target: number
}

export const useCart = (scope?: string): UseCartReturnType => {
  const key = storageKey(scope)
  const [items, setItems] = useState<CartItemType[]>(() => readCart(key))

  // Le scope peut changer sans démontage (passage d'un parcours à froid à un groupe créé).
  useEffect(() => {
    setItems(readCart(key))
  }, [key])

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(items))
    } catch {
      // Écriture impossible : le panier reste fonctionnel pour la session en cours.
    }
  }, [key, items])

  const add = useCallback((item: CartItemType) => {
    setItems((current) =>
      current.some((existing) => existing.productCode === item.productCode) ? current : [...current, item],
    )
  }, [])

  const remove = useCallback((productCode: string) => {
    setItems((current) => current.filter((item) => item.productCode !== productCode))
  }, [])

  const has = useCallback((productCode: string) => items.some((item) => item.productCode === productCode), [items])

  const toggle = useCallback(
    (item: CartItemType) => {
      if (has(item.productCode)) {
        remove(item.productCode)
      } else {
        add(item)
      }
    },
    [add, has, remove],
  )

  // Purge écrite tout de suite plutôt que déléguée à l'effet de persistance : `clear` est appelé
  // juste avant une navigation, qui démonte le composant avant que l'effet n'ait pu s'exécuter.
  const clear = useCallback(() => {
    setItems([])
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Suppression impossible : l'effet de persistance repassera si le composant reste monté.
    }
  }, [key])

  return useMemo(
    () => ({ items, add, remove, toggle, has, clear, count: items.length, target: CART_TARGET }),
    [items, add, remove, toggle, has, clear],
  )
}
